import { enqueuePlayerGroupsSync } from '@/lib/realtime-sync/plugins/player-groups';
import type { PlayerGroup, PlayerGroupId, PlayerId, UserId } from '@/schemas';
import {
  PlayerGroupSchema,
  PlayerGroupId as PlayerGroupIdSchema,
  PlayerGroupNameSchema,
  PlayerGroupDescriptionSchema,
  createPlayerGroupId,
} from '@/schemas';
import { db } from './client';

export const normalizePlayerGroupName = (name: string): string => {
  // eslint-disable-next-line unicorn/prefer-string-replace-all -- regex is intentional for collapsing whitespace
  return PlayerGroupNameSchema.parse(name).trim().replace(/\s+/g, ' ');
};

const normalizeDescription = (description?: string | null): string | undefined => {
  if (!description) return undefined;
  // Normalize description: trim and collapse whitespace (similar to name normalization)
  // eslint-disable-next-line unicorn/prefer-string-replace-all -- regex is intentional for collapsing whitespace
  const normalized = description.trim().replace(/\s+/g, ' ');
  const parsed = PlayerGroupDescriptionSchema.safeParse(normalized);
  return parsed.success ? parsed.data : undefined;
};

const dedupePlayerIds = (playerIds: readonly PlayerId[]): PlayerId[] => {
  return [...new Set(playerIds)];
};

export type CreatePlayerGroupInput = {
  readonly name: string;
  readonly description?: string;
  readonly playerIds: readonly PlayerId[];
  readonly userId?: UserId;
  readonly avatarImageId?: import('@/schemas/ids').ImageId;
};

export const createPlayerGroup = async ({
  name,
  description,
  playerIds,
  userId,
  avatarImageId,
}: CreatePlayerGroupInput): Promise<PlayerGroupId> => {
  const id = createPlayerGroupId();
  const now = Date.now();

  const group: PlayerGroup = {
    id,
    name: normalizePlayerGroupName(name),
    description: normalizeDescription(description),
    playerIds: dedupePlayerIds(playerIds),
    userId,
    avatarImageId,
    createdAt: now,
    updatedAt: now,
    deletedAt: undefined,
  };

  await db.playerGroups.add(PlayerGroupSchema.parse(group));
  if (group.userId) {
    enqueuePlayerGroupsSync(group.userId);
  }
  return id;
};

export const listPlayerGroups = async (): Promise<PlayerGroup[]> => {
  const groups = await db.playerGroups.filter((group) => !group.deletedAt).toArray();
  return groups.map((group) => PlayerGroupSchema.parse(group));
};

export const getPlayerGroup = async (groupId: PlayerGroupId): Promise<PlayerGroup | undefined> => {
  const id = PlayerGroupIdSchema.parse(groupId);
  const group = await db.playerGroups.get(id);
  if (!group) return undefined;
  if (group.deletedAt) return undefined;
  return PlayerGroupSchema.parse(group);
};

export type UpdatePlayerGroupInput = {
  readonly name?: string;
  readonly description?: string | null;
  readonly playerIds?: readonly PlayerId[];
  readonly avatarImageId?: import('@/schemas/ids').ImageId | null;
};

export const updatePlayerGroup = async (
  groupId: PlayerGroupId,
  input: UpdatePlayerGroupInput,
): Promise<void> => {
  const id = PlayerGroupIdSchema.parse(groupId);
  const existing = await db.playerGroups.get(id);
  if (!existing || existing.deletedAt) {
    throw new Error('Player group not found');
  }

  const now = Date.now();

  const name = input.name === undefined ? existing.name : normalizePlayerGroupName(input.name);
  const description =
    input.description === undefined
      ? existing.description
      : normalizeDescription(input.description ?? undefined);
  const playerIds =
    input.playerIds === undefined ? existing.playerIds : dedupePlayerIds(input.playerIds);
  let avatarImageId = existing.avatarImageId;
  if (input.avatarImageId !== undefined) {
    avatarImageId = input.avatarImageId === null ? undefined : input.avatarImageId;
  }

  const updated: PlayerGroup = {
    ...existing,
    name,
    description,
    playerIds,
    avatarImageId,
    updatedAt: now,
  };

  await db.playerGroups.put(PlayerGroupSchema.parse(updated));
  if (updated.userId) {
    enqueuePlayerGroupsSync(updated.userId);
  }
};

export const deletePlayerGroup = async (groupId: PlayerGroupId): Promise<void> => {
  const id = PlayerGroupIdSchema.parse(groupId);
  const existing = await db.playerGroups.get(id);
  if (!existing || existing.deletedAt) {
    throw new Error('Player group not found');
  }

  const now = Date.now();
  const deleted: PlayerGroup = {
    ...existing,
    deletedAt: now,
    updatedAt: now,
  };

  await db.playerGroups.put(PlayerGroupSchema.parse(deleted));
  if (deleted.userId) {
    enqueuePlayerGroupsSync(deleted.userId);
  }
};

/**
 * Restore a deleted player group
 */
export const restorePlayerGroup = async (groupId: PlayerGroupId): Promise<void> => {
  const id = PlayerGroupIdSchema.parse(groupId);
  const existing = await db.playerGroups.get(id);
  if (!existing || !existing.deletedAt) {
    throw new Error('Player group not found or not deleted');
  }

  const now = Date.now();
  const restored: PlayerGroup = {
    ...existing,
    deletedAt: undefined,
    updatedAt: now,
  };

  await db.playerGroups.put(PlayerGroupSchema.parse(restored));
  if (restored.userId) {
    enqueuePlayerGroupsSync(restored.userId);
  }
};
